import { useState, useEffect } from "react";
import { PageHeader } from "../../page-header";
import { usePagamentoLote } from "../../../hooks/usePagamentoLote";
import { formatValueToMonetary } from "../../../utils/formatter";
import { formatCnpjCpf } from "../../../utils/formatter";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { getPixInfo } from "../../../services/pix.service";
import { identificarTipoChavePix } from "../../../utils/identificarTipoChavePix";
import { banco_codigo as bancoCodigo } from "../../../assets/banco_codigo";
import { apiV1 } from "../../../lib/axios";
import { SearchableSelect } from "./SearchableSelect";
import "../PagamentoLote.css";
import "./PagamentoLoteNovo.css";

const PAYMENT_FIELDS = {
  PIX: [
    { id: "paymentDetail", label: "Chave Pix", placeholder: "CPF, CNPJ, e-mail, telefone ou chave aleatória" },
  ],
  BOLETO: [
    { id: "paymentDetail", label: "Código de barras / linha digitável", placeholder: "Digite ou cole o código do boleto" },
    { id: "supplierData",  label: "Cedente", placeholder: "Nome do cedente do boleto" },
  ],
};

const EMPTY_FORM = {
  type: "PIX",
  payee: "",
  document: "",
  amount: "",
  dueDate: "",
  costCenter: "",
  paymentDetail: "",
  supplierData: "",
  invoice: "",
  note: "",
  // TED specific
  tedBank: "",
  tedAgency: "",
  tedAccount: "",
  tedDigit: "",
  tedAccountType: "0",
};

function dateBR(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("pt-BR");
}

function typeIcon(type) {
  const icons = { BOLETO: "▥", PIX: "❖", TRIBUTO: "▣", TED: "↔" };
  return icons[type] || "▣";
}

export default function PagamentoLoteNovo() {
  const { addPayment, createBatch, payments, batches, loadPayments, payees } = usePagamentoLote();
  const [form, setForm] = useState(EMPTY_FORM);
  const [mode, setMode] = useState("existente");
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [currentBatchId, setCurrentBatchId] = useState(null);
  const [selectedPayeeId, setSelectedPayeeId] = useState("");
  const [manualMode, setManualMode] = useState(false);
  const [pixKeyStatus, setPixKeyStatus] = useState("idle");
  const [pixKeyReceiver, setPixKeyReceiver] = useState(null);
  const [barcodeStatus, setBarcodeStatus] = useState("idle");
  const [barcodeInfo, setBarcodeInfo] = useState(null);

  const openBatches = batches.filter(
    (b) => b.status === "RASCUNHO" || b.status === "AGUARDANDO_APROVACAO"
  );

  useEffect(() => {
    if (mode === "existente" && selectedBatchId) {
      setCurrentBatchId(selectedBatchId);
      loadPayments(selectedBatchId);
    }
  }, [selectedBatchId]);

  useEffect(() => {
    setCurrentBatchId(null);
    setSelectedBatchId("");
  }, [mode]);

  function handleChange(e) {
    let { name, value } = e.target;
    if (name === "document") {
      value = formatCnpjCpf(value);
    }
    if (name === "amount") {
      // Allow only digits, build currency mask: 1234 -> 12,34
      const digits = value.replace(/\D/g, "");
      if (digits === "") { value = ""; }
      else {
        const num = parseInt(digits, 10) / 100;
        value = num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
    }
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === "paymentDetail" || name === "type") {
      setPixKeyStatus("idle");
      setPixKeyReceiver(null);
      setBarcodeStatus("idle");
      setBarcodeInfo(null);
    }
  }

  function handlePayeeSelect(payeeId) {
    setSelectedPayeeId(payeeId);
    if (!payeeId || payeeId === "MANUAL") {
      setManualMode(payeeId === "MANUAL");
      setForm(EMPTY_FORM);
      setPixKeyStatus("idle"); setPixKeyReceiver(null);
      setBarcodeStatus("idle"); setBarcodeInfo(null);
      return;
    }
    setManualMode(false);
    const payee = payees.find((p) => String(p.id) === String(payeeId));
    if (!payee) return;
    const type = payee.preferredType || "PIX";
    setPixKeyStatus("idle"); setPixKeyReceiver(null);
    setBarcodeStatus("idle"); setBarcodeInfo(null);
    setForm((prev) => ({
      ...prev,
      payee: payee.name || "",
      document: formatCnpjCpf(payee.document || ""),
      type,
      paymentDetail: type === "PIX" ? (payee.pixKey || "") : "",
      tedBank: type === "TED" ? (payee.bank || "") : prev.tedBank,
      tedAgency: type === "TED" ? (payee.agency || "") : prev.tedAgency,
      tedAccount: type === "TED" ? (payee.account || "") : prev.tedAccount,
      tedDigit: type === "TED" ? (payee.accountDigit || payee.digit || "") : prev.tedDigit,
      tedAccountType: type === "TED" ? (payee.accountType ?? "0") : prev.tedAccountType,
    }));
  }

  // Auto-validate PIX key when it gets filled by payee selection
  useEffect(() => {
    if (selectedPayeeId && form.type === "PIX" && form.paymentDetail) {
      validatePixKey(form.paymentDetail);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPayeeId, form.paymentDetail, form.type]);

  async function validatePixKey(key) {
    if (!key) return;
    setPixKeyStatus("validating");
    setPixKeyReceiver(null);
    try {
      const response = await getPixInfo(key, identificarTipoChavePix(key));
      const data = response?.data?.data;
      setPixKeyReceiver(data?.ReceiverName || "Titular encontrado");
      setPixKeyStatus("valid");
    } catch {
      setPixKeyStatus("invalid");
    }
  }

  async function validateBarcode(barcode) {
    if (!barcode) return;
    setBarcodeStatus("validating");
    setBarcodeInfo(null);
    try {
      const response = await apiV1.get(`/payment/info-cip/fitbank/${barcode}`);
      const ticket = response?.data?.ticket;
      if (!ticket?.barcode) throw new Error("Boleto inválido");
      setBarcodeInfo(ticket);
      setBarcodeStatus("valid");
      setForm((prev) => ({
        ...prev,
        amount: prev.amount || String(ticket.totalValue || ticket.value || ""),
        dueDate: prev.dueDate || (ticket.dueDate ? ticket.dueDate.split("T")[0] : ""),
        supplierData: prev.supplierData || ticket.assignor || ticket.receiverName || "",
      }));
    } catch {
      setBarcodeStatus("invalid");
    }
  }

  function clearForm() {
    setForm(EMPTY_FORM);
    setSelectedPayeeId("");
    setManualMode(false);
    setPixKeyStatus("idle");
    setPixKeyReceiver(null);
    setBarcodeStatus("idle");
    setBarcodeInfo(null);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const { type, payee, document, amount, dueDate, paymentDetail, supplierData,
            tedBank, tedAgency, tedAccount, tedDigit, tedAccountType } = form;

    if (!type || !payee || !document || !amount || !dueDate) {
      toast.error("Preencha todos os campos obrigatórios.");
      return;
    }
    if (type === "PIX" && !paymentDetail) {
      toast.error("Preencha todos os campos obrigatórios.");
      return;
    }
    if (type === "BOLETO" && (!paymentDetail || !supplierData)) {
      toast.error("Preencha todos os campos obrigatórios.");
      return;
    }
    if (type === "TED" && (!tedBank || !tedAgency || !tedAccount || !tedDigit)) {
      toast.error("Preencha todos os campos do TED.");
      return;
    }
    if (type === "PIX" && pixKeyStatus !== "valid") {
      toast.error("Valide a chave Pix antes de prosseguir.");
      return;
    }
    if (type === "BOLETO" && barcodeStatus !== "valid") {
      toast.error("Valide o código de barras antes de prosseguir.");
      return;
    }
    if (mode === "existente" && !selectedBatchId) {
      toast.error("Selecione o lote ao qual deseja adicionar o pagamento.");
      return;
    }

    const tedAccountLabel = tedAccountType === "0" || tedAccountType === 0 ? "Conta Corrente" : "Conta Poupança";
    const tedBankLabel = bancoCodigo.find((b) => String(b.value) === String(tedBank))?.label || tedBank;

    const details =
      type === "PIX"
        ? paymentDetail
        : type === "BOLETO"
        ? `${paymentDetail} | Cedente: ${supplierData}`
        : JSON.stringify({
            toBank: tedBank,
            toBankBranch: tedAgency,
            toBankAccount: tedAccount,
            toBankAccountDigit: tedDigit,
            accountType: Number(tedAccountType),
          });

    let batchId = mode === "existente" ? selectedBatchId : null;

    if (mode === "novo") {
      const batchResult = await createBatch();
      if (!batchResult?.ok) return;
      batchId = batchResult.batch.id;
    }

    const result = await addPayment({
      type,
      payee,
      document: document.replace(/\D/g, ""),
      amount: Number(String(amount).replace(/\./g, "").replace(",", ".")),
      dueDate,
      costCenter: form.costCenter,
      details,
      invoice: form.invoice || null,
      note: form.note || null,
      batchId,
    });

    if (result?.ok) {
      toast.success(mode === "novo" ? "Lote criado com sucesso!" : "Conta adicionada ao lote com sucesso!");
      if (mode === "novo") setCurrentBatchId(batchId);
      clearForm();
    }
  }

  return (
    <div className="paylote-page">
      <div className="paylote-inner col-md-10 m-auto mt-4">
        <div className="mb-3">
          <PageHeader
            title="Novo Lote"
            pages={[
              { name: "Pagamento em Lote", link: "/pagamento-lote/dashboard" },
              { name: "Novo lote" },
            ]}
          />
        </div>

        {/* Form card */}
        <section className="paylote-section-card">
          <div className="paylote-section-header">
            <div>
              <h2>Cadastrar conta no lote</h2>
              <p className="paylote-subtitle">
                Escolha o tipo de pagamento para ver os campos específicos.
              </p>
            </div>
            <button className="paylote-btn-secondary" type="button" onClick={clearForm}>
              Limpar campos
            </button>
          </div>

          {/* Mode selector */}
          <div className="paylote-tabs" style={{ marginBottom: "1.25rem" }}>
            <button
              type="button"
              className={`paylote-tab${mode === "existente" ? " active" : ""}`}
              onClick={() => setMode("existente")}
            >
              Adicionar a lote existente
            </button>
            <button
              type="button"
              className={`paylote-tab${mode === "novo" ? " active" : ""}`}
              onClick={() => setMode("novo")}
            >
              + Criar novo lote
            </button>
          </div>

          {mode === "existente" && (
            <div className="paylote-form-field paylote-novo-batch-select">
              <label>Selecionar lote *</label>
              <select value={selectedBatchId} onChange={(e) => setSelectedBatchId(e.target.value)}>
                <option value="">— Selecione um lote aberto —</option>
                {openBatches.map((b) => (
                  <option key={b.id} value={b.id}>
                    #{b.id} — {b.createdByName} — {b.totalBills} contas ({b.status})
                  </option>
                ))}
              </select>
              {openBatches.length === 0 && (
                <span className="paylote-novo-field-hint">Nenhum lote aberto disponível no momento.</span>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Payee selector */}
            <div className="paylote-form-field" style={{ marginBottom: "1rem" }}>
              <label>Favorecido</label>
              <select
                value={selectedPayeeId}
                onChange={(e) => handlePayeeSelect(e.target.value)}
              >
                <option value="">— Selecione um favorecido —</option>
                <option value="MANUAL">✏️ Inserir manualmente</option>
                {payees.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — {p.document}{p.preferredType ? ` (${p.preferredType})` : ""}
                  </option>
                ))}
              </select>
              {!selectedPayeeId && (
                <span className="paylote-novo-field-hint">
                  {payees.length === 0
                    ? "Nenhum favorecido cadastrado. Selecione \"Inserir manualmente\" para continuar."
                    : "Selecione um favorecido pré-cadastrado ou escolha \"Inserir manualmente\"."}
                </span>
              )}
            </div>

            {/* Fields only shown after payee is selected or manual mode */}
            {(selectedPayeeId && selectedPayeeId !== "") && (<>

            {/* Common fields */}
            <div className="paylote-form-grid">
              <div className="paylote-form-field">
                <label>Tipo de pagamento *</label>
                <select name="type" value={form.type} onChange={handleChange} disabled={!!selectedPayeeId && selectedPayeeId !== "MANUAL"}>
                  <option value="PIX">PIX</option>
                  <option value="BOLETO">Boleto</option>
                  <option value="TED">TED</option>
                </select>
              </div>
              <div className="paylote-form-field">
                <label>Favorecido / fornecedor *</label>
                <input name="payee" value={form.payee} onChange={handleChange} placeholder="Nome do fornecedor" readOnly={!!selectedPayeeId && selectedPayeeId !== "MANUAL"} style={selectedPayeeId && selectedPayeeId !== "MANUAL" ? { background: "var(--input-disabled-bg, #f1f5f9)", cursor: "not-allowed" } : undefined} />
              </div>
              <div className="paylote-form-field">
                <label>CPF / CNPJ *</label>
                <input name="document" value={form.document} onChange={handleChange} placeholder="00.000.000/0001-00" readOnly={!!selectedPayeeId && selectedPayeeId !== "MANUAL"} style={selectedPayeeId && selectedPayeeId !== "MANUAL" ? { background: "var(--input-disabled-bg, #f1f5f9)", cursor: "not-allowed" } : undefined} />
              </div>
              <div className="paylote-form-field">
                <label>Valor *</label>
                <input
                  name="amount"
                  inputMode="numeric"
                  value={form.amount}
                  onChange={handleChange}
                  placeholder="0,00"
                />
              </div>
              <div className="paylote-form-field">
                <label>Vencimento *</label>
                <input name="dueDate" type="date" value={form.dueDate} onChange={handleChange} />
              </div>
              <div className="paylote-form-field">
                <label>Centro de custo</label>
                <input name="costCenter" value={form.costCenter} onChange={handleChange} placeholder="Ex: Marketing, Operações..." />
              </div>
            </div>

            {/* Dynamic fields by type */}
            {form.type !== "TED" ? (
              <div className="paylote-form-grid two">
                {(PAYMENT_FIELDS[form.type] || PAYMENT_FIELDS.PIX).map((field) => (
                  <div className="paylote-form-field" key={field.id}>
                    <label>{field.label}{field.optional ? "" : " *"}</label>
                    <input
                      name={field.id}
                      value={form[field.id]}
                      onChange={handleChange}
                      placeholder={field.placeholder}
                      readOnly={!!selectedPayeeId && selectedPayeeId !== "MANUAL" && field.id === "paymentDetail" && form.type === "PIX"}
                      style={selectedPayeeId && selectedPayeeId !== "MANUAL" && field.id === "paymentDetail" && form.type === "PIX" ? { background: "var(--input-disabled-bg, #f1f5f9)", cursor: "not-allowed" } : undefined}
                      onBlur={
                        field.id === "paymentDetail" && form.type === "PIX" && selectedPayeeId === "MANUAL"
                          ? () => validatePixKey(form.paymentDetail)
                          : field.id === "paymentDetail" && form.type === "BOLETO"
                          ? () => validateBarcode(form.paymentDetail)
                          : undefined
                      }
                    />
                    {field.id === "paymentDetail" && form.type === "PIX" && (
                      <>
                        {pixKeyStatus === "validating" && (
                          <span className="paylote-pix-hint paylote-pix-validating">⏳ Validando chave Pix...</span>
                        )}
                        {pixKeyStatus === "valid" && (
                          <span className="paylote-pix-hint paylote-pix-valid">✓ {pixKeyReceiver}</span>
                        )}
                        {pixKeyStatus === "invalid" && (
                          <span className="paylote-pix-hint paylote-pix-invalid">✗ Chave Pix não encontrada</span>
                        )}
                      </>
                    )}
                    {field.id === "paymentDetail" && form.type === "BOLETO" && (
                      <>
                        {barcodeStatus === "validating" && (
                          <span className="paylote-pix-hint paylote-pix-validating">⏳ Consultando boleto...</span>
                        )}
                        {barcodeStatus === "valid" && (
                          <span className="paylote-pix-hint paylote-pix-valid">✓ Boleto válido — {barcodeInfo?.assignor || barcodeInfo?.receiverName || "Cedente identificado"}</span>
                        )}
                        {barcodeStatus === "invalid" && (
                          <span className="paylote-pix-hint paylote-pix-invalid">✗ Código de barras não encontrado ou inválido</span>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              /* TED specific fields */
              <div className="paylote-form-grid">
                <div className="paylote-form-field paylote-ted-bank">
                  <label>Banco *</label>
                  <SearchableSelect
                    value={form.tedBank}
                    onChange={(val) => setForm((prev) => ({ ...prev, tedBank: val }))}
                    options={bancoCodigo.map((b) => ({ value: b.value, label: b.label }))}
                    placeholder="— Selecione o banco —"
                    disabled={!!selectedPayeeId && selectedPayeeId !== "MANUAL"}
                  />
                </div>
                <div className="paylote-form-field">
                  <label>Agência *</label>
                  <input
                    name="tedAgency"
                    value={form.tedAgency}
                    onChange={handleChange}
                    placeholder="0000"
                    maxLength={6}
                    readOnly={!!selectedPayeeId && selectedPayeeId !== "MANUAL"}
                    style={selectedPayeeId && selectedPayeeId !== "MANUAL" ? { background: "var(--input-disabled-bg, #f1f5f9)", cursor: "not-allowed" } : undefined}
                  />
                </div>
                <div className="paylote-form-field">
                  <label>Conta *</label>
                  <input
                    name="tedAccount"
                    value={form.tedAccount}
                    onChange={handleChange}
                    placeholder="00000000"
                    maxLength={20}
                    readOnly={!!selectedPayeeId && selectedPayeeId !== "MANUAL"}
                    style={selectedPayeeId && selectedPayeeId !== "MANUAL" ? { background: "var(--input-disabled-bg, #f1f5f9)", cursor: "not-allowed" } : undefined}
                  />
                </div>
                <div className="paylote-form-field paylote-ted-digit">
                  <label>Dígito *</label>
                  <input
                    name="tedDigit"
                    value={form.tedDigit}
                    onChange={handleChange}
                    placeholder="0"
                    maxLength={1}
                    readOnly={!!selectedPayeeId && selectedPayeeId !== "MANUAL"}
                    style={selectedPayeeId && selectedPayeeId !== "MANUAL" ? { background: "var(--input-disabled-bg, #f1f5f9)", cursor: "not-allowed" } : undefined}
                  />
                </div>
                <div className="paylote-form-field">
                  <label>Tipo de conta *</label>
                  <select name="tedAccountType" value={form.tedAccountType} onChange={handleChange} disabled={!!selectedPayeeId && selectedPayeeId !== "MANUAL"}>
                    <option value="0">Conta Corrente</option>
                    <option value="1">Conta Poupança</option>
                  </select>
                </div>
              </div>
            )}

            {/* Note and invoice */}
            <div className="paylote-form-grid two">
              <div className="paylote-form-field">
                <label>Nota fiscal</label>
                <input name="invoice" value={form.invoice} onChange={handleChange} placeholder="Número da NF, link do PDF ou chave de acesso (opcional)" />
              </div>
              <div className="paylote-form-field">
                <label>Descrição / Observação</label>
                <input name="note" value={form.note} onChange={handleChange} placeholder="Ex: pagamento referente à campanha de maio" />
              </div>
            </div>

            <div className="paylote-novo-form-actions">
              <button className="paylote-btn-success" type="submit">
                {mode === "novo" ? "+ Criar lote com esta conta" : "+ Adicionar ao lote selecionado"}
              </button>
              <Link to="/pagamento-lote/aprovacao" className="paylote-btn-secondary paylote-novo-link">
                Ver aprovação
              </Link>
            </div>
            </>)}
          </form>
        </section>

        {/* Preview table — só exibe quando há um lote ativo */}
        {currentBatchId && <section className="paylote-table-card">
          <div className="paylote-selection-bar">
            <strong className="paylote-novo-section-title">
              Contas cadastradas neste lote ({payments.length})
            </strong>
          </div>
          <table>
            <thead>
              <tr>
                <th>Favorecido</th>
                <th>Tipo</th>
                <th>Vencimento</th>
                <th>Valor</th>
                <th>Status</th>
                <th>Nota fiscal</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr><td colSpan={6}>
                  <div className="paylote-empty-state">
                    <p>Nenhuma conta cadastrada ainda.</p>
                  </div>
                </td></tr>
              ) : payments.map((item) => (
                <tr key={item.id}>
                  <td>
                    <div className="paylote-payee-name">{item.payee}</div>
                    <div className="paylote-payee-doc">{formatCnpjCpf(item.document)}</div>
                  </td>
                  <td>
                    <span className="paylote-type-badge">
                      {typeIcon(item.type)} {item.type}
                    </span>
                  </td>
                  <td className={item.situation === "Vencido" ? "paylote-novo-due-vencido" : "paylote-novo-due-normal"}>
                    {dateBR(item.dueDate)}
                  </td>
                  <td><strong>{formatValueToMonetary(item.amount)}</strong></td>
                  <td>
                    <span className={`paylote-badge ${(item.status || "pendente").toLowerCase()}`}>
                      {item.status || "Pendente"}
                    </span>
                  </td>
                  <td className="paylote-novo-invoice-muted">{item.invoice || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>}
      </div>
    </div>
  );
}
